/**
 * @file Shared detail-view section renderers for parent-board details.
 */

import { appData } from '../../app/state/state.js';
import {
    findItem,
} from "../utils/itemLookup.js";
import {
    formatAndSanitizeUserContent,
} from "../utils/contentUtils.js";
import {
    formatDateTime,
} from "../utils/dateUtils.js";
import {
    getUserDisplayNameById,
} from "../utils/userDisplay.js";

let lastAuditTrailHtml = '';
let lastAuditTrailItemId = null;

/**
 * Renders the reusable long-text display/edit launcher block.
 * @param {object} params Section parameters.
 * @param {string} params.fieldName Data field name.
 * @param {string} params.label Display label.
 * @param {string} params.value Current raw field value.
 * @param {string} params.wrapperClass Grid/span classes for the section.
 * @returns {string} Long-text section HTML.
 */
export function renderLongTextDetailSection({
    fieldName,
    label = 'Details',
    value = '',
    wrapperClass = '',
}) {
    const escapedValue = sanitizeTextareaValue(value);
    return `
                <div class="${wrapperClass} long-text-field-container relative" data-field-name="${fieldName}">
                    <div class="mb-1">
                        <span class="block text-base text-gray-500 sm:text-sm">${label}</span>
                    </div>
                    <textarea name="${fieldName}" class="hidden" data-long-text-value data-native-field-skip="true" aria-hidden="true" tabindex="-1">${escapedValue}</textarea>
                    <div class="relative">
                        <div class="long-text-display min-h-[128px] max-h-[200px] overflow-y-auto rounded-md border border-gray-200 bg-white p-2 text-base text-gray-700 transition-all duration-200 hover:border-[var(--dfc-brand-primary)] focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300" role="textbox" tabindex="0" aria-label="${label}">${formatAndSanitizeUserContent(value)}</div>
                        <div class="overflow-indicator absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white/90 to-transparent dark:from-gray-700/90 flex justify-center items-end pb-1 pointer-events-none opacity-0 transition-opacity duration-200">
                             <span class="material-symbols-outlined text-[18px] leading-none text-gray-800 dark:text-gray-200">expand_more</span>
                        </div>
                    </div>
                </div>
    `;
}

function sanitizeTextareaValue(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
}

/**
 * Renders cached audit trail metadata for a detail item.
 * @param {object|null} item Current detail item.
 * @returns {string} Audit trail HTML.
 */
export function renderAuditTrail(item) {
    if (!item) {return lastAuditTrailHtml || '';}

    const createdByUid = item.createdBy;
    const updatedAt = item.updatedAt;
    const updatedByUid = item.updatedBy || createdByUid;

    const _users = appData?.users || [];
    const creator = getUserDisplayNameById(createdByUid);
    const updater = getUserDisplayNameById(updatedByUid);

    const createdStr = createdByUid
        ? (item.createdAt ? `Created: ${creator} on ${formatDateTime(item.createdAt)}` : `Created: ${creator}`)
        : '';

    const updatedStr = (updatedByUid && updatedAt)
        ? `Last Updated: ${updater} on ${formatDateTime(updatedAt)}`
        : '';

    if (!updatedAt && lastAuditTrailItemId === item.id && lastAuditTrailHtml) {
        return lastAuditTrailHtml;
    }

    if (!createdStr && !updatedStr) {return '';}

    const html = `
        <div id="audit-trail-container" class="mt-3 text-[11px] text-gray-400 flex flex-wrap gap-x-6">
            ${createdStr ? `<span class="flex items-center"><span class="material-symbols-outlined text-[12px] mr-1 opacity-60">history</span>${createdStr}</span>` : ''}
            ${updatedStr ? `<span class="flex items-center"><span class="material-symbols-outlined text-[12px] mr-1 opacity-60">edit_note</span>${updatedStr}</span>` : ''}
        </div>
    `;

    lastAuditTrailHtml = html;
    lastAuditTrailItemId = item.id;

    return html;
}

/**
 * Refreshes the rendered audit trail without a full detail re-render.
 * @param {string} itemId Item id whose audit trail should be refreshed.
 */
export function updateAuditTrailUI(itemId) {
    if (!itemId) {return;}
    const container = document.getElementById('audit-trail-container');
    if (!container) {return;}

    const item = findItem(itemId);
    if (!item || !item.updatedAt) {return;}

    lastAuditTrailItemId = null;
    lastAuditTrailHtml = '';

    container.outerHTML = renderAuditTrail(item);
}
